import { c as _c } from "react/compiler-runtime";
import { FleetCategoryLink } from '@/components/FleetCategory/FleetCategoryLink';
import { FleetCategoryStarRating } from '@/components/FleetCategory/FleetCategoryStarRating';
import { FleetTypeCardImagesCarousel } from '@/components/FleetTypeCardImagesCarousel';
import { Typography } from '@/components/Typography';
import { KeyFeatures } from '@/components/blocks/cms/FilteredFleetTypeCardsBlock/KeyFeatures';
import { ToggleCompare } from '@/components/blocks/cms/VehicleCompareBlock/ToggleCompare';
import ModalSearchFormBtn from '@/components/forms/ModalSearchFormBtn';
import { cn } from '@/lib/cn';
import { formatCurrency } from '@/lib/formatCurrency';
import type { CmsFleetCategory } from '@/services/cms-sdk/';
import type { CmsFleetCategoryDataTag } from '@/services/cms-sdk/schemas/models/CmsFleetCategoryDataTagSchema';
export interface TagProps {
  tag: CmsFleetCategoryDataTag | undefined;
  className?: string;
}
function Tag(t0) {
  const $ = _c(9);
  const {
    tag,
    className
  } = t0;
  if (!tag) {
    return null;
  }
  const t1 = tag.style === "secondary";
  const t2 = tag.style === "primary";
  let t3;
  if ($[0] !== className || $[1] !== t1 || $[2] !== t2) {
    t3 = cn("bg-base-150 rounded-full px-4 py-2 absolute top-4 left-4", {
      "bg-tag-content-secondary": t1,
      "bg-tag-content-primary": t2
    }, className);
    $[0] = className;
    $[1] = t1;
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== tag.name) {
    t4 = <Typography variant="paragraph" className="truncate">{tag.name}</Typography>;
    $[4] = tag.name;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== t3 || $[7] !== t4) {
    t5 = <div className={t3}>{t4}</div>;
    $[6] = t3;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  return t5;
}
export interface FleetTypeCardProps {
  fleetCategory: CmsFleetCategory;
  preload?: boolean;
  vehicleCTALabel?: string;
}
export function FleetTypeCard(t0) {
  const $ = _c(46);
  const {
    fleetCategory,
    preload,
    vehicleCTALabel
  } = t0;
  let t1;
  if ($[0] !== fleetCategory.name) {
    t1 = <Typography as="span" variant="h4" className="line-clamp-1">{fleetCategory.name}</Typography>;
    $[0] = fleetCategory.name;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const title = t1;
  let t2;
  if ($[2] !== fleetCategory.data.tag) {
    t2 = <Tag tag={fleetCategory.data.tag} className="z-1" />;
    $[2] = fleetCategory.data.tag;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== fleetCategory) {
    t3 = <div className="absolute top-4 right-4 z-1"><ToggleCompare fleetCategory={fleetCategory} /></div>;
    $[4] = fleetCategory;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== fleetCategory || $[7] !== preload) {
    t4 = <FleetTypeCardImagesCarousel fleetCategory={fleetCategory} width={488} height={300} className="aspect-488/300 object-cover rounded-t-2xl" preload={preload} />;
    $[6] = fleetCategory;
    $[7] = preload;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t2 || $[10] !== t3 || $[11] !== t4) {
    t5 = <div className="rounded-t-2 relative">{t2}{t3}{t4}</div>;
    $[9] = t2;
    $[10] = t3;
    $[11] = t4;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== fleetCategory.rating) {
    t6 = <FleetCategoryStarRating rating={fleetCategory.rating} />;
    $[13] = fleetCategory.rating;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== fleetCategory || $[16] !== title) {
    t7 = <FleetCategoryLink fleetCategory={fleetCategory}>{title}</FleetCategoryLink>;
    $[15] = fleetCategory;
    $[16] = title;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  const t8 = fleetCategory.data?.ageDescription;
  let t9;
  if ($[18] !== t8) {
    t9 = <Typography className="line-clamp-1" variant="paragraph">{t8}</Typography>;
    $[18] = t8;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== t6 || $[21] !== t7 || $[22] !== t9) {
    t10 = <div className="flex flex-col gap-1.5">{t6}{t7}{t9}</div>;
    $[20] = t6;
    $[21] = t7;
    $[22] = t9;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  const t11 = fleetCategory.data?.keyFeatures;
  let t12;
  if ($[24] !== t11) {
    t12 = <KeyFeatures keyFeatures={t11} />;
    $[24] = t11;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  let t13;
  if ($[26] !== t10 || $[27] !== t12) {
    t13 = <div className="flex flex-col gap-5">{t10}{t12}</div>;
    $[26] = t10;
    $[27] = t12;
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  let t14;
  if ($[29] !== fleetCategory.code || $[30] !== fleetCategory.fleetTypeSlug) {
    t14 = {
      fleetType: fleetCategory.fleetTypeSlug,
      fleetCategoryCode: fleetCategory.code
    };
    $[29] = fleetCategory.code;
    $[30] = fleetCategory.fleetTypeSlug;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  const t15 = vehicleCTALabel || "Book now";
  let t16;
  if ($[32] !== t14 || $[33] !== t15) {
    t16 = <ModalSearchFormBtn className="btn btn-primary px-7.5 py-6" values={t14} openStep="locations">{t15}</ModalSearchFormBtn>;
    $[32] = t14;
    $[33] = t15;
    $[34] = t16;
  } else {
    t16 = $[34];
  }
  let t17;
  if ($[35] !== fleetCategory.estimatedDailyRate) {
    t17 = fleetCategory.estimatedDailyRate ? <div className="flex flex-col"><Typography variant="caption">from</Typography><Typography className="font-normal" variant="h6">{formatCurrency(fleetCategory.estimatedDailyRate, {
          minimumFractionDigits: 2
        })}/day</Typography></div> : null;
    $[35] = fleetCategory.estimatedDailyRate;
    $[36] = t17;
  } else {
    t17 = $[36];
  }
  let t18;
  if ($[37] !== t16 || $[38] !== t17) {
    t18 = <div className="flex flex-row gap-5 items-center">{t16}{t17}</div>;
    $[37] = t16;
    $[38] = t17;
    $[39] = t18;
  } else {
    t18 = $[39];
  }
  let t19;
  if ($[40] !== t13 || $[41] !== t18) {
    t19 = <div className="flex flex-col gap-6 p-4">{t13}{t18}</div>;
    $[40] = t13;
    $[41] = t18;
    $[42] = t19;
  } else {
    t19 = $[42];
  }
  let t20;
  if ($[43] !== t19 || $[44] !== t5) {
    t20 = <article className="flex flex-col rounded-2xl border-1 border-base-300 hover:border-primary overflow-hidden">{t5}{t19}</article>;
    $[43] = t19;
    $[44] = t5;
    $[45] = t20;
  } else {
    t20 = $[45];
  }
  return t20;
}